/**
 * SEO on-page per il sito pubblico (solo pagine pubbliche, non aree dietro login).
 * Gestisce: title, meta description, canonical, robots, Open Graph, Twitter Card, JSON-LD.
 *
 * Pensato per essere ricco e "AI-friendly":
 *  - meta description sempre presente quando fornita
 *  - canonical assoluto (utile per crawler e LLM)
 *  - OG completo (type, title, description, image, image:alt, url, site_name, locale)
 *  - Twitter Card summary_large_image
 *  - meta article:* per ogType="article" (blog post): published/modified time, author, section, tag
 *  - keywords (utile per crawler e per LLM che li sfruttano in indicizzazione)
 *  - JSON-LD strutturato (Organization, Article, FAQPage, BreadcrumbList, …) come array o oggetto
 */
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL as PROD_SITE_URL } from '../../config/siteUrl';
import { toCanonicalUrl } from '../../utils/canonicalUrl';

const DEFAULT_SITE_NAME = 'Virtual Clinic';
const DEFAULT_LOGO_CDN = 'https://pamasoft.b-cdn.net/virtualclinic/images/virtual-clinic-logo-orizzontale.png';
const DEFAULT_IMAGE = DEFAULT_LOGO_CDN;
const TWITTER_HANDLE = process.env.REACT_APP_TWITTER_HANDLE || '';

const getBaseUrl = () => PROD_SITE_URL;

const toAbsoluteUrl = (url, baseUrl) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
};

/**
 * @param {Object} props
 * @param {string} props.title - Titolo pagina (max ~60 caratteri). Se manca, usa default.
 * @param {string} [props.description] - Meta description (max ~155-160 caratteri).
 * @param {string} [props.canonicalPath] - Path canonical (es. '/medici'). Se non passato, niente canonical.
 * @param {string} [props.ogImage] - URL immagine per Open Graph / Twitter.
 * @param {string} [props.ogImageAlt] - Testo alt dell'immagine OG (accessibilità + grado di trust per LLM).
 * @param {('website'|'article'|'profile'|'product'|'video')} [props.ogType='website'] - Tipo Open Graph.
 * @param {boolean} [props.noIndex] - Se true, robots noindex,nofollow.
 * @param {string} [props.keywords] - Keywords separate da virgola.
 * @param {object|object[]} [props.jsonLd] - Oggetto o array di oggetti JSON-LD.
 * @param {Object} [props.article] - Meta per ogType="article".
 * @param {string} [props.article.publishedTime] - ISO datetime.
 * @param {string} [props.article.modifiedTime] - ISO datetime.
 * @param {string} [props.article.author] - Nome autore.
 * @param {string} [props.article.section] - Sezione (es. categoria).
 * @param {string[]} [props.article.tags] - Tag.
 * @param {string} [props.locale='it_IT'] - Locale Open Graph.
 * @param {string} [props.linkPrev] - Path rel="prev" (es. '/blog' o '/blog/page/2').
 * @param {string} [props.linkNext] - Path rel="next".
 */
const PublicSEO = ({
  title,
  description,
  canonicalPath: canonicalPathProp,
  ogImage,
  ogImageAlt,
  ogType = 'website',
  noIndex = false,
  keywords,
  jsonLd,
  article,
  locale = 'it_IT',
  linkPrev,
  linkNext,
}) => {
  const baseUrl = getBaseUrl();
  const canonical = canonicalPathProp
    ? toCanonicalUrl(baseUrl, canonicalPathProp)
    : null;
  const imageUrl = toAbsoluteUrl(ogImage || DEFAULT_IMAGE, baseUrl);
  const fullTitle = title
    ? (title.includes(DEFAULT_SITE_NAME) ? title : `${title} | ${DEFAULT_SITE_NAME}`)
    : `${DEFAULT_SITE_NAME} - Telemedicina e visite mediche online`;
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  /** index.html ha meta baseline per i bot: Helmet ne aggiunge altre con data-rh → rimuovi i duplicati statici. */
  useEffect(() => {
    if (!description) return;
    const stripStatic = (selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (!el.hasAttribute('data-rh') && !el.getAttribute('data-rh')) {
          el.remove();
        }
      });
    };
    stripStatic('meta[name="description"]');
    stripStatic('meta[property="og:description"]');
    stripStatic('meta[name="twitter:description"]');
  }, [description]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} key="canonical" />}
      {linkPrev && <link rel="prev" href={toCanonicalUrl(baseUrl, linkPrev)} key="prev" />}
      {linkNext && <link rel="next" href={toCanonicalUrl(baseUrl, linkNext)} key="next" />}
      <meta
        name="robots"
        content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'}
      />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical || baseUrl} />
      <meta property="og:image" content={imageUrl} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:site_name" content={DEFAULT_SITE_NAME} />
      <meta property="og:locale" content={locale} />

      {/* Open Graph article (per blog post / news) */}
      {ogType === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {ogType === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {ogType === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {ogType === 'article' && article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {ogType === 'article' && Array.isArray(article?.tags) && article.tags.map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />
      {ogImageAlt && <meta name="twitter:image:alt" content={ogImageAlt} />}
      {TWITTER_HANDLE && <meta name="twitter:site" content={TWITTER_HANDLE} />}

      {/* JSON-LD: un blocco per ogni elemento, così è più facile da parsare per i validatori */}
      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default PublicSEO;
